/* eslint-disable stars-web/no-direct-browser-globals-access */
import { ClientDevice } from '@ps/platform-message-framework/common/ClientDevice';
import { OSId } from '@ps/platform-message-framework/common/OSId';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { DebugKey, createDebug } from '@ps/web-core-utils';
import * as Sentry from '@sentry/react';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useFirstRender from '@swf/core/hooks/useFirstRender';
import { select } from '@swf/core/store';

import { LicenseNames, toMIAWLocale } from '../../utils';

const contextPath = '/assets/htdocs/sitecontext.html';

const debug = createDebug(DebugKey.Embedded);

const SalesforceLoader: React.FC = () => {
  const dispatch = useDispatch();
  const locale = useSelector(select.locale.get);
  const { idpToken } = useSelector(select.session.get);
  const userIntId = useSelector(select.account.getUserIntId);
  const site = useSelector(select.cms.getSiteName);
  const licence = useSelector(select.account.getLicense);
  const licenseName = LicenseNames[licence] ? LicenseNames[licence] : site;
  const isSFAuth = useSelector(select.help.isSFAuthenticated);
  const showButton = useFeature(FeatureFlag.SFShowButton);
  const chatLocale = toMIAWLocale(locale);
  const isEmbedded = useSelector(select.device.isDesktopEmbedded);
  const deviceId = useSelector(select.device.getId);
  const osId = useSelector(select.device.getOS);
  const device = ClientDevice[deviceId];
  const os = OSId[osId];
  const deviceName = device + ' ' + os;

  // Track current warmup session to prevent race conditions
  const warmupSessionRef = useRef<number>(0);
  // Track if final SDK has been initialized to prevent duplicates
  const finalSdkInitializedRef = useRef<boolean>(false);
  // Track the current auth state to detect changes
  const currentAuthStateRef = useRef<boolean>(false);
  // Track current warmup listener for cleanup
  const currentWarmupListenerRef = useRef<EventListener | null>(null);
  // Track if warmup is in progress
  const warmupInProgressRef = useRef<boolean>(false);
  // Track timeout for when warmup is done
  const warmupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track current auth values to avoid stale closures
  const currentIdpTokenRef = useRef<string | undefined>(idpToken);
  const currentIsSFAuthRef = useRef<boolean>(isSFAuth);

  const { salesforceSrc, salesforceScrt, salesforceDeploymentCode, salesforceDeploymentName } =
    useSelector(select.help.getSFChatInfo);
  const {
    salesforceSrcUnauthenticated: warmupSalesforceSrc,
    salesforceSrcAuthenticated: authSalesforceSrc,
    salesforceScrt: warmupSalesforceScrt,
    salesforceDeploymentCode: warmupSalesforceDeploymentCode,
    salesforceDeploymentNameUnauthenticated: warmupSalesforceDeploymentName,
    salesforceUnauthSdk,
    salesforceAuthSdk
  } = useSelector(select.config.get);

  const isFirstRender = useFirstRender();

  // ---------------------------------------------------------------------------
  // Helpers.
  // ---------------------------------------------------------------------------

  const setEmbeddedMessagingReady = () => {
    const bootstrap = window.embeddedservice_bootstrap;

    if (!bootstrap) {
      return;
    }

    const warmupScript = document.getElementById('SFScriptW');
    if (warmupScript) {
      debug(
        'setEmbeddedMessagingReady: found warmup script just loaded already, so, removing it..'
      );
      warmupScript.parentNode?.removeChild(warmupScript);
    }

    if (idpToken && userIntId && bootstrap.userVerificationAPI?.setIdentityToken) {
      bootstrap.userVerificationAPI.setIdentityToken({
        identityTokenType: 'JWT',
        identityToken: idpToken
      });
    }

    const prechatFields = {
      ...(userIntId && { PlayerID: userIntId.toString() }),
      Language: chatLocale,
      LicenceType: licenseName,
      DeviceType: isEmbedded ? 'Desktop_App' : 'Web',
      DeviceName: deviceName
    };

    bootstrap.prechatAPI?.setHiddenPrechatFields(prechatFields);

    // SDK is now ready - dispatch to Redux store
    dispatch.help.setSFSdkReady({ isSFSdkReady: true });
    debug('setEmbeddedMessagingReady: SDK is ready, dispatched to store');
  };

  const initEmbeddedMessaging = () => {
    if (
      !salesforceDeploymentCode ||
      !salesforceDeploymentName ||
      !salesforceSrc ||
      !salesforceScrt
    ) {
      return;
    }

    const bootstrap = window.embeddedservice_bootstrap;

    if (bootstrap) {
      bootstrap.settings.language = chatLocale;
      bootstrap.settings.restrictSessionOnMessagingChannel = true;
      bootstrap.settings.shouldMinimizeWindowOnNewTab = true;
      bootstrap.settings.hideChatButtonOnLoad = !showButton;
    }

    window.addEventListener('onEmbeddedMessagingReady', setEmbeddedMessagingReady as EventListener);

    bootstrap?.init(salesforceDeploymentCode, salesforceDeploymentName, salesforceSrc, {
      scrt2URL: salesforceScrt
    });
  };

  const createMIAWScript = (bootstrapPath: string) => {
    debug('createMIAWScript: creating main SDK script %s', bootstrapPath);

    if (!bootstrapPath) {
      debug('createMIAWScript: no bootstrap path provided');
      return;
    }

    // Extra cleanup before creating the final SDK to prevent any lingering elements
    const existingMessagingDivs = document.querySelectorAll('.embedded-messaging');
    if (existingMessagingDivs.length > 0) {
      debug(
        'createMIAWScript: cleaning up %d existing messaging divs',
        existingMessagingDivs.length
      );
    }
    existingMessagingDivs.forEach((el) => {
      el.parentNode?.removeChild(el);
    });

    const script = document.createElement('script');
    script.id = 'SFScript';
    script.src = bootstrapPath;
    script.type = 'text/javascript';

    script.onload = () => {
      debug('createMIAWScript: main SDK script loaded successfully');

      finalSdkInitializedRef.current = true;

      let retryCount = 0;
      const maxRetries = 3;
      const tryInit = () => {
        try {
          initEmbeddedMessaging();
        } catch (error) {
          retryCount++;
          if (retryCount < maxRetries) {
            debug('createMIAWScript: init failed, retrying in 1s');
            setTimeout(tryInit, 1000);
          } else {
            debug('createMIAWScript: init failed after 3 attempts');
            throw error;
          }
        }
      };
      tryInit();
    };
    script.onerror = (error) => {
      debug('createMIAWScript: main SDK script failed to load %o', error);
      Sentry.captureException(
        new Error(`Salesforce main SDK script failed to load: ${bootstrapPath}`),
        {
          tags: {
            'salesforce.sdk': 'main',
            'salesforce.script.path': bootstrapPath
          },
          extra: {
            error,
            finalSdkInitialized: finalSdkInitializedRef.current,
            currentAuthState: currentAuthStateRef.current
          }
        }
      );
    };
    script.async = true;

    document.body.appendChild(script);
  };

  const cleanSharedStorage = () => {
    const iframe = document.getElementById('siteContextFrameUnAuth') as HTMLIFrameElement;

    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          method: 'ESW_3RDPARTY_STORAGE_CLEAR',
          data: salesforceDeploymentCode
        },
        '*'
      );
    }
  };

  const warmupDone = (sessionId: number) => {
    debug('warmupDone: called for session %d (current: %d)', sessionId, warmupSessionRef.current);

    // Clear warmup timeout if exists
    if (warmupTimeoutRef.current) {
      clearTimeout(warmupTimeoutRef.current);
      warmupTimeoutRef.current = null;
    }

    // Mark warmup as no longer in progress
    warmupInProgressRef.current = false;

    // Ignore callbacks from old sessions
    if (sessionId !== warmupSessionRef.current) {
      debug('warmupDone: ignoring stale callback from session %d', sessionId);
      return;
    }

    const warmupScript = document.getElementById('SFScriptW');
    if (warmupScript) {
      debug('warmupDone: removing warmup script');
      warmupScript.parentNode?.removeChild(warmupScript);
    }

    const bootstrap = window.embeddedservice_bootstrap;

    if (!bootstrap) {
      debug('warmupDone: no bootstrap found');
      return;
    }

    // CRITICAL: Use ref values to get current auth state instead of stale closure values
    const currentIdpToken = currentIdpTokenRef.current;
    const currentIsSFAuth = currentIsSFAuthRef.current;
    const authSnapshot = !!currentIdpToken || currentIsSFAuth;
    debug('warmupDone: auth state is %s', authSnapshot ? 'authenticated' : 'unauthenticated');

    if (salesforceAuthSdk && salesforceUnauthSdk) {
      // Check if userVerificationAPI exists before using it
      if (bootstrap.userVerificationAPI?.clearSession) {
        bootstrap.userVerificationAPI
          .clearSession()
          .then(() => {
            // Double-check we're still the current session after async operation
            if (sessionId !== warmupSessionRef.current) {
              return;
            }

            cleanSharedStorage();
            cleanDOM();

            const selectedSdk = authSnapshot ? salesforceAuthSdk : salesforceUnauthSdk;

            debug(
              'warmupDone: selected %s SDK %s',
              authSnapshot ? 'authenticated' : 'unauthenticated',
              selectedSdk
            );

            Sentry.addBreadcrumb({
              message: 'Salesforce SDK selection in warmupDone',
              category: 'salesforce.sdk',
              level: 'info',
              data: {
                authSnapshot,
                selectedSdk,
                isAuthSdk: selectedSdk === salesforceAuthSdk,
                isUnauthSdk: selectedSdk === salesforceUnauthSdk,
                session: sessionId
              }
            });

            createMIAWScript(selectedSdk);
          })
          .catch((error) => {
            Sentry.captureException(new Error('Salesforce clearSession failed'), {
              tags: {
                'salesforce.operation': 'clearSession'
              },
              extra: {
                error,
                session: sessionId,
                authSnapshot,
                hasUserVerificationAPI: !!bootstrap.userVerificationAPI,
                hasClearSession: !!bootstrap.userVerificationAPI?.clearSession
              }
            });
          });
      } else {
        // If userVerificationAPI doesn't exist, proceed without clearing session
        // Add small delay to ensure proper cleanup
        setTimeout(() => {
          if (sessionId !== warmupSessionRef.current) {
            return;
          }

          cleanSharedStorage();
          cleanDOM();

          const selectedSdk = authSnapshot ? salesforceAuthSdk : salesforceUnauthSdk;

          Sentry.addBreadcrumb({
            message: 'Salesforce SDK selection in warmupDone (no userVerificationAPI)',
            category: 'salesforce.sdk',
            level: 'warning',
            data: {
              authSnapshot,
              selectedSdk,
              isAuthSdk: selectedSdk === salesforceAuthSdk,
              isUnauthSdk: selectedSdk === salesforceUnauthSdk,
              session: sessionId
            }
          });

          createMIAWScript(selectedSdk);
        }, 100);
      }
    }
  };

  const initWarmup = (sessionId: number) => {
    // ALWAYS use unauthenticated deployment settings during warmup
    const deploymentCode = warmupSalesforceDeploymentCode;
    const deploymentName = warmupSalesforceDeploymentName;
    const deploymentSrc = warmupSalesforceSrc;
    const deploymentScrt = warmupSalesforceScrt;

    if (!deploymentCode || !deploymentName || !deploymentSrc || !deploymentScrt) {
      return;
    }

    const bootstrap = window.embeddedservice_bootstrap;

    if (bootstrap) {
      bootstrap.settings.language = chatLocale;
      bootstrap.settings.restrictSessionOnMessagingChannel = true;
      bootstrap.settings.shouldMinimizeWindowOnNewTab = true;
      bootstrap.settings.hideChatButtonOnLoad = true;
    }

    // Remove any existing warmup listener before adding new one
    if (currentWarmupListenerRef.current) {
      window.removeEventListener('onEmbeddedMessagingReady', currentWarmupListenerRef.current);
    }

    // Create and store the new warmup listener
    const warmupListener = (() => {
      warmupDone(sessionId);
    }) as EventListener;
    currentWarmupListenerRef.current = warmupListener;

    window.addEventListener('onEmbeddedMessagingReady', warmupListener, {
      once: true
    });

    // Add timeout fallback in case event never fires
    warmupTimeoutRef.current = setTimeout(() => {
      if (sessionId === warmupSessionRef.current && currentWarmupListenerRef.current) {
        debug('initWarmup: onEmbeddedMessagingReady timeout, forcing warmupDone');
        window.removeEventListener('onEmbeddedMessagingReady', warmupListener);
        currentWarmupListenerRef.current = null;
        warmupDone(sessionId);
      }
    }, 5000);

    bootstrap?.init(deploymentCode, deploymentName, deploymentSrc, { scrt2URL: deploymentScrt });
  };

  const cleanDOM = () => {
    // Remove the main embedded messaging ready listener
    window.removeEventListener(
      'onEmbeddedMessagingReady',
      setEmbeddedMessagingReady as EventListener
    );

    // Remove current warmup listener if exists
    if (currentWarmupListenerRef.current) {
      window.removeEventListener('onEmbeddedMessagingReady', currentWarmupListenerRef.current);
      currentWarmupListenerRef.current = null;
    }

    // Clear any existing bootstrap references
    if (window.embeddedservice_bootstrap) {
      try {
        window.embeddedservice_bootstrap.removeEventHandlers?.();
      } catch (e) {
        debug('cleanDOM: error removing event handlers: %o', e);
      }
    }

    const ids = [
      'inert-script',
      'embedded-messaging',
      'embeddedMessagingSiteContextFrame',
      'SFScript',
      'SFScriptW',
      'embeddedMessagingFilePreviewFrame'
    ];

    // Remove ALL instances of each ID (in case of duplicates)
    ids.forEach((id) => {
      let el = document.getElementById(id);
      while (el) {
        el.parentNode?.removeChild(el);
        el = document.getElementById(id);
      }
    });

    // Also remove elements by class name as Salesforce creates multiple divs with class "embedded-messaging"
    const embeddedMessagingDivs = document.querySelectorAll('.embedded-messaging');
    embeddedMessagingDivs.forEach((el) => {
      el.parentNode?.removeChild(el);
    });

    // Remove duplicate iframes
    const contextFrames = document.querySelectorAll('.embeddedMessagingSiteContextFrame');
    contextFrames.forEach((el) => {
      el.parentNode?.removeChild(el);
    });

    const filePreviewFrames = document.querySelectorAll('.embeddedMessagingFilePreviewFrame');
    filePreviewFrames.forEach((el) => {
      el.parentNode?.removeChild(el);
    });

    if (salesforceDeploymentCode) {
      localStorage.removeItem(`${salesforceDeploymentCode}_WEB_STORAGE`);
      sessionStorage.removeItem(`${salesforceDeploymentCode}_WEB_STORAGE`);
    }

    // Reset the final SDK initialized flag when cleaning up
    finalSdkInitializedRef.current = false;

    // SDK is no longer ready - dispatch to Redux store
    dispatch.help.setSFSdkReady({ isSFSdkReady: false });
    debug('cleanDOM: SDK cleaned up, dispatched ready=false to store');
  };

  const warmup = () => {
    // Prevent concurrent warmups
    if (warmupInProgressRef.current) {
      debug('warmup: warmup already in progress, skipping');
      return;
    }

    warmupInProgressRef.current = true;

    // Increment session to invalidate any pending callbacks
    warmupSessionRef.current += 1;
    const currentSession = warmupSessionRef.current;

    const isAuthenticated = !!idpToken || isSFAuth;
    // ALWAYS load unauth SDK first during warmup, regardless of auth state
    const sdkToLoad = salesforceUnauthSdk;

    debug(
      'warmup: starting session %d with unauthenticated SDK %s (auth state: %s)',
      currentSession,
      sdkToLoad,
      isAuthenticated ? 'authenticated' : 'unauthenticated'
    );

    if (!sdkToLoad) {
      debug('warmup: no unauth SDK available to load');
      return;
    }

    // Clean up any existing warmup script before creating new one
    const existingWarmupScript = document.getElementById('SFScriptW');
    if (existingWarmupScript) {
      debug('warmup: removing existing warmup script');
      existingWarmupScript.parentNode?.removeChild(existingWarmupScript);
    }

    const script = document.createElement('script');
    script.id = 'SFScriptW';
    script.src = sdkToLoad;
    script.type = 'text/javascript';

    script.onload = () => {
      debug('warmup: warmup SDK script loaded successfully for session %d', currentSession);
      // Add a small delay to ensure SDK is fully loaded
      setTimeout(() => {
        // Double-check session is still current
        if (currentSession === warmupSessionRef.current) {
          initWarmup(currentSession);
        } else {
          debug('warmup: session changed during script load, skipping init');
        }
      }, 100);
    };
    script.onerror = (error) => {
      debug('warmup: warmup SDK script failed to load %o', error);
      warmupInProgressRef.current = false;
      Sentry.captureException(
        new Error(`Salesforce warmup SDK script failed to load: ${sdkToLoad}`),
        {
          tags: {
            'salesforce.sdk': 'warmup',
            'salesforce.script.path': sdkToLoad
          },
          extra: {
            error,
            isAuthenticated,
            session: currentSession,
            isAuthSdk: sdkToLoad === salesforceAuthSdk,
            isUnauthSdk: sdkToLoad === salesforceUnauthSdk
          }
        }
      );
    };
    script.async = true;

    document.body.appendChild(script);
  };

  // ---------------------------------------------------------------------------
  // React lifecycle hooks
  // ---------------------------------------------------------------------------

  // Keep auth refs updated
  useEffect(() => {
    currentIdpTokenRef.current = idpToken;
  }, [idpToken]);

  useEffect(() => {
    currentIsSFAuthRef.current = isSFAuth;
  }, [isSFAuth]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!salesforceDeploymentCode) {
      debug('useEffect[isSFAuth]: no deployment code, skipping');
      return;
    }

    // Check if auth state has changed
    const currentAuthState = !!idpToken || isSFAuth;
    const authStateChanged = currentAuthStateRef.current !== currentAuthState;

    debug(
      'useEffect[isSFAuth]: auth state changed: %s (final SDK initialized: %s)',
      authStateChanged,
      finalSdkInitializedRef.current
    );

    // If final SDK is already initialized AND auth state hasn't changed, skip
    if (finalSdkInitializedRef.current && !authStateChanged) {
      debug(
        'useEffect[isSFAuth]: skipping re-initialization - SDK initialized and auth state unchanged'
      );
      // Ensure SDK is ready in Redux
      if (window.embeddedservice_bootstrap?.utilAPI) {
        dispatch.help.setSFSdkReady({ isSFSdkReady: true });
      }
      return;
    }

    // CRITICAL: If auth state changed and warmup is in progress, we need to cancel it
    if (authStateChanged && warmupInProgressRef.current) {
      // Increment session to invalidate any pending callbacks
      warmupSessionRef.current += 1;
      // Clear timeout if exists
      if (warmupTimeoutRef.current) {
        clearTimeout(warmupTimeoutRef.current);
        warmupTimeoutRef.current = null;
      }
      // Mark warmup as not in progress so new one can start
      warmupInProgressRef.current = false;
    }

    // Update current auth state
    currentAuthStateRef.current = currentAuthState;

    debug(
      'useEffect[isSFAuth]: starting warmup for %s state',
      currentAuthStateRef.current ? 'authenticated' : 'unauthenticated'
    );

    window.embeddedservice_bootstrap?.removeEventHandlers();
    cleanDOM();
    warmup();
  }, [isSFAuth]);

  /* eslint-disable arrow-body-style */
  useEffect(() => {
    if (
      idpToken &&
      !isFirstRender &&
      window.embeddedservice_bootstrap?.userVerificationAPI?.setIdentityToken
    ) {
      window.embeddedservice_bootstrap.userVerificationAPI.setIdentityToken({
        identityTokenType: 'JWT',
        identityToken: idpToken
      });
    }
  }, [idpToken]);

  /* eslint-enable arrow-body-style */
  /* eslint-disable arrow-body-style */
  useEffect(() => {
    return () => {
      if (warmupTimeoutRef.current) {
        clearTimeout(warmupTimeoutRef.current);
        warmupTimeoutRef.current = null;
      }

      warmupInProgressRef.current = false;

      if (
        window.embeddedservice_bootstrap?.userVerificationAPI?.clearSession &&
        window.embeddedservice_bootstrap.removeEventHandlers
      ) {
        window.embeddedservice_bootstrap.removeEventHandlers();
        cleanSharedStorage();
      }
      cleanDOM();
    };
  }, []);
  /* eslint-enable arrow-body-style */
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <div hidden>
      <iframe
        title="UnauthIframe"
        id="siteContextFrameUnAuth"
        src={(warmupSalesforceSrc || '').concat(contextPath)}
      />
      <iframe
        title="AuthIframe"
        id="siteContextFrameAuth"
        src={(authSalesforceSrc || '').concat(contextPath)}
      />
    </div>
  );
};

export default SalesforceLoader;
